import { formatYearWeek } from "@appretio/shared";
import { type Option } from "@appretio/ui-kit";
import type { ForecastMetaDataType } from "../../hooks/useFetchForecastMetaData";
import { formatFiscalWeek } from "../../utils/formatFiscalWeek";
import type { ForecastTableControlValues } from "../tableControls/forecastTableControlValues";

/** A week option that also names the fiscal year the grid shows the week under. */
export interface GridWeekOption extends Option {
  fiscalYear: string;
}

/**
 * Week options for the training-range / horizon pickers, spanning exactly
 * the fiscal years the loaded grid shows. Every week comes from
 * `weekPeriodMapping` (the 454-restated calendar): which weeks a fiscal year
 * has, W53 included, and each week's calendar `year_week` — the value the
 * generate request takes. The key is read, not derived: the restated
 * boundary drifts against any fixed calendar-week offset, and a horizon
 * derived one week late names a week of the *next* fiscal year, which the
 * engine then forecasts as a year of its own. Years outside the restated
 * horizon contribute nothing. Labels follow the page's week-label toggle:
 * fiscal (`2024/2025 W07`, the grid's row + column address) or calendar
 * (`2024 W38`); the year is always spelled out because, unlike a grid
 * header, a list entry has no fiscal-year row to be read against.
 */
export function buildGridWeekOptions(
  fiscalYears: string[],
  weekPeriodMapping: ForecastMetaDataType["weekPeriodMapping"] | undefined,
  weekLabels: ForecastTableControlValues["weekLabels"]
): GridWeekOption[] {
  if (!weekPeriodMapping) return [];
  const options: { value: number; label: string; fiscalYear: string }[] = [];
  for (const fiscalYear of fiscalYears) {
    for (let week = 1; week <= 53; week++) {
      const weekName = `W${String(week).padStart(2, "0")}`;
      const periods = weekPeriodMapping[`${fiscalYear}${weekName}`];
      if (!periods) continue;
      options.push({
        value: periods.yearWeek,
        fiscalYear,
        label:
          weekLabels === "Fiscal"
            ? formatFiscalWeek(fiscalYear, week)
            : formatYearWeek(periods.yearWeek)
      });
    }
  }
  return options
    .sort((a, b) => a.value - b.value)
    .map(({ value, label, fiscalYear }) => ({
      label,
      value: String(value),
      fiscalYear
    }));
}

/** The three week pickers' options, each narrowed to the weeks that keep the range valid. */
export interface RangeWeekOptions {
  trainFrom: GridWeekOption[];
  trainTo: GridWeekOption[];
  forecastTo: GridWeekOption[];
}

/**
 * Narrows each week picker against the other two, so a pick cannot break
 * `train from ≤ train to < horizon`. Training also stops before the last
 * offered week, which the horizon needs.
 */
export function narrowRangeWeekOptions(
  options: GridWeekOption[],
  picks: {
    trainFrom: Option | undefined;
    trainTo: Option | undefined;
    forecastTo: Option | undefined;
  },
  bounds: {
    /** The first week the horizon may end on: earlier weeks hold history, never forecast. */
    forecastFrom: number | undefined;
    /** Training stops before this week: the actuals end at the last closed week. */
    historyEndsBefore: number | undefined;
  }
): RangeWeekOptions {
  const trainFrom = weekOf(picks.trainFrom) ?? -Infinity;
  const trainTo = weekOf(picks.trainTo);
  const trainEndsBefore = Math.min(
    weekOf(picks.forecastTo ?? options[options.length - 1]) ?? Infinity,
    bounds.historyEndsBefore ?? Infinity
  );
  const trainable = options.filter(
    (option) => Number(option.value) < trainEndsBefore
  );
  return {
    trainFrom: trainable.filter(
      (option) => Number(option.value) <= (trainTo ?? Infinity)
    ),
    trainTo: trainable.filter((option) => Number(option.value) >= trainFrom),
    forecastTo: options.filter(
      (option) =>
        Number(option.value) > (trainTo ?? trainFrom) &&
        Number(option.value) >= (bounds.forecastFrom ?? -Infinity)
    )
  };
}

function weekOf(option: Option | undefined): number | undefined {
  return option ? Number(option.value) : undefined;
}
